/**
 * liveSessionFormatter — pure derivations off `UILiveSession`.
 *
 * Companion of `dateFormatter`: where that one formats a date, this one answers the
 * questions the live-session surfaces ask about a session. No `@data` / store / service
 * imports, so DS components and stories can call it directly.
 */
import moment from "moment"

import { formatDayTime, formatDuration, formatHour, isToday, minutesUntil } from "./dateFormatter"
import { capitalizeFirst, numberInWords } from "./textFormatter"
import { type UILiveSession, type UILiveSessionStatus, type UISubject } from "../types"
import { type UITutorSessionStudent } from "../types/tutorCoquilleAccueil"

/**
 * Client-side mirror of the backend rule `AttendanceFlag::$minDelayToDeclareAbsenceInHours = 2`
 * (`app/Models/AttendanceFlag.php`), applied by `LiveOccurrence::canRecoverAbsence()`, which
 * answers `reason_slug: 'delay_too_short'`.
 *
 * The copy exists so the parent is told "trop tard" *before* entering a dead end — it never
 * replaces the server's answer: the same screen must also be shown when the API refuses with
 * `delay_too_short`. If the backend constant moves, this one has to move with it.
 */
export const MOVE_CUTOFF_MINUTES = 120

/** False when the session starts in less than 2 h (or has already started) — it can no longer be moved. */
export function canMoveSession(session: Pick<UILiveSession, "date">): boolean {
    return minutesUntil(session.date) >= MOVE_CUTOFF_MINUTES
}

/**
 * L'heure limite pour décaler une séance d'AUJOURD'HUI, telle qu'on la dit au parent : son début
 * moins `MOVE_CUTOFF_MINUTES` — « 15h19 » pour une séance à 17h19. Elle n'a d'intérêt que le jour
 * même : pour une séance des jours suivants, la limite est loin et la mention chargeait la liste
 * (Wajih, 28/09) — `null`. `null` aussi quand elle est passée : la séance porte alors « Dans moins
 * de 2 h », et les deux ne s'affichent jamais ensemble (même frontière que `canMoveSession`).
 */
export function moveCutoffLabel(session: Pick<UILiveSession, "date">): string | null {
    if (!isToday(session.date) || !canMoveSession(session)) return null
    const cutoff = moment(session.date).subtract(MOVE_CUTOFF_MINUTES, "minutes")
    return formatHour(cutoff.toISOString())
}

/**
 * Les types de salle dont le rdv se règle par le formulaire d'absence — copie littérale de
 * `isGroupSession` (`StudentLiveCardComponent`). Ce qui n'est pas là se règle avec quelqu'un :
 * le tuteur pour un individuel, un support ou un live personnalisé, l'équipe pour un essai.
 */
export const GROUP_ROOM_TYPES = ["biweekly", "study_group", "holiday_course", "holiday_workbook"]

/** Vrai quand la séance est un cours en groupe : seuls ceux-là se déplacent vers un rattrapage. */
export function isGroupSession(session: Pick<UILiveSession, "roomType">): boolean {
    return GROUP_ROOM_TYPES.includes(session.roomType ?? "")
}

/**
 * Les états qui éteignent l'entrée en live, chacun pris à son origine côté élève :
 *  - `finished` = l'occurrence est clôturée (`finalized_at`) — « Cette session est clôturée » ;
 *  - `absent` = une absence est notée, et le bouton « GO ! » disparaît (`!hasAbsenceFlag`) ;
 *  - `payment_blocked` = le bouton se verrouille et ouvre la régularisation, pas le live.
 */
const NO_ENTRY_STATUSES: UILiveSessionStatus[] = ["finished", "absent", "payment_blocked"]

/**
 * Vrai quand la séance est SUSPENDUE faute de paiement.
 *
 * Ce n'est pas un état de la séance mais de la famille : tant qu'il dure, plus rien ne se fait
 * sur aucune séance — ni entrer, ni déplacer, ni prévenir d'une absence. Toucher une de ces
 * séances n'ouvre donc pas la feuille d'actions ; on propose la régularisation.
 */
export function isPaymentBlocked(session: Pick<UILiveSession, "statuses">): boolean {
    return session.statuses.includes("payment_blocked")
}

/**
 * Vrai quand une absence est signalée sur cette séance — elle reste dans les listes (c'est d'elle
 * que part « Annuler l'absence »), mais grisée : on la voit tout de suite comme différente.
 */
export function isAbsenceReported(session: Pick<UILiveSession, "statuses">): boolean {
    return session.statuses.includes("absent")
}

/**
 * Vrai quand la séance est clôturée (`finalized_at`) — un fait, pas une heure passée. Comme une
 * séance où l'absence est signalée, elle quitte l'accueil et reste dans « Tous mes lives »,
 * estompée : seul son badge « Terminé » garde sa couleur.
 */
export function isSessionFinished(session: Pick<UILiveSession, "statuses">): boolean {
    return session.statuses.includes("finished")
}

/**
 * Vrai quand une ligne de séance s'estompe : l'absence est signalée, ou la séance est terminée. Sa
 * ligne horaire passe à l'encre secondaire, ses visages et ses matières s'estompent, et seul son badge
 * garde sa couleur — chez le parent comme chez le masteur, dont la ligne nomme ses élèves.
 */
export function isSessionDimmed(session: Pick<UILiveSession, "statuses">): boolean {
    return isAbsenceReported(session) || isSessionFinished(session)
}

/**
 * Vrai quand l'élève pourrait entrer dans cette séance MAINTENANT.
 *
 * Règle reprise telle quelle du bouton « GO ! » de la carte de l'élève
 * (`StudentLiveCardComponent.js:628-686`, réécrite à l'identique par
 * `NextLiveSessionBanner.js:246-266`) : **le live n'est accessible que le jour même**
 * — hors de ce jour, l'élève reçoit « Le live n'est accessible que le jour-même » au lieu de
 * l'écran d'entrée. Il n'y a donc pas de fenêtre en minutes : toute la journée compte, y compris
 * une séance commencée, et c'est `finalized_at` qui ferme la porte à la fin.
 *
 * Les deux chemins de l'élève mènent à la même route (`pre-call`, qui porte le test d'entrée) :
 * offrir ce geste au parent hors de cette règle l'enverrait sur une impasse, de l'autre côté
 * d'une bascule de compte. `now` (en millisecondes) est l'horloge de l'appelant.
 */
export function canJoinLiveSession(
    session: Pick<UILiveSession, "date" | "statuses">,
    now: number = Date.now()
): boolean {
    if (session.statuses.some((status) => NO_ENTRY_STATUSES.includes(status))) return false
    return isToday(session.date, now)
}

/** La fin de la séance, en millisecondes : son début plus sa durée. */
function sessionEnd(session: Pick<UILiveSession, "date" | "duration">): number {
    return moment(session.date).valueOf() + session.duration * 60_000
}

/** Vrai de la minute où la séance commence à celle où elle finit. */
export function isSessionInProgress(
    session: Pick<UILiveSession, "date" | "duration">,
    now: number = Date.now()
): boolean {
    return now >= moment(session.date).valueOf() && now < sessionEnd(session)
}

/**
 * Vrai tant que l'HEURE peut encore changer ce que la séance montre — le décompte, « En cours »,
 * « Rejoindre » : le jour même, jusqu'à sa fin, et seulement là où l'enfant peut entrer
 * (`canJoinLiveSession`). C'est ce qui fait tourner l'horloge d'une séance à l'écran, et l'arrête.
 */
export function isSessionClockLive(
    session: Pick<UILiveSession, "date" | "duration" | "statuses">,
    now: number = Date.now()
): boolean {
    return canJoinLiveSession(session, now) && now < sessionEnd(session)
}

/** Combien de minutes avant le début la carte de la prochaine séance montre « Rejoindre ». */
export const JOIN_BUTTON_OPENS_MINUTES = 15

/**
 * Vrai quand la carte de la prochaine séance montre son bouton « Rejoindre » : de
 * `JOIN_BUTTON_OPENS_MINUTES` avant le début jusqu'à la fin, et seulement là où la feuille
 * d'actions offre « Rejoindre en tant que … » — c'est `canJoinLiveSession` qui le dit (jour même,
 * ni absence signalée, ni paiement en attente, ni séance clôturée). La feuille, elle, garde sa
 * ligne toute la journée : le bouton n'est que l'heure où le geste devient LE geste de la carte.
 */
export function isJoinWindowOpen(
    session: Pick<UILiveSession, "date" | "duration" | "statuses">,
    now: number = Date.now()
): boolean {
    if (!canJoinLiveSession(session, now)) return false
    const opens = moment(session.date).valueOf() - JOIN_BUTTON_OPENS_MINUTES * 60_000
    return now >= opens && now < sessionEnd(session)
}

/**
 * Le décompte du jour même, à côté du badge « Aujourd'hui » : « dans 1 h 42 », puis « dans
 * 42 min » sous l'heure. `null` quand il n'y a rien à décompter : hors du jour même, dès que la
 * séance commence (« En cours » est son état, `sessionBadges`), et là où l'enfant ne peut pas
 * entrer (`canJoinLiveSession` : absence signalée, séance suspendue ou clôturée).
 *
 * Les minutes restantes sont arrondies AU-DESSUS : à 1 h 41 min 30 s du début, la carte dit encore
 * « dans 1 h 42 », et elle ne dit jamais « dans 0 min ». `now` (en millisecondes) est l'horloge de
 * l'appelant, qui la fait avancer à chaque minute (`isSessionClockLive`).
 */
export function sessionCountdown(
    session: Pick<UILiveSession, "date" | "duration" | "statuses">,
    now: number = Date.now()
): string | null {
    if (!canJoinLiveSession(session, now)) return null

    const msUntilStart = moment(session.date).valueOf() - now
    if (msUntilStart <= 0) return null

    const minutes = Math.ceil(msUntilStart / 60_000)
    if (minutes < 60) return `dans ${minutes} min`
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest === 0 ? `dans ${hours} h` : `dans ${hours} h ${String(rest).padStart(2, "0")}`
}

/** "Sarah Dupont" → "Sarah D." — the tutor name every live surface prints. */
export function tutorShortName(tutor: { firstname: string; lastname?: string }): string {
    const lastInitial = tutor.lastname ? `${tutor.lastname.charAt(0)}.` : ""
    return [tutor.firstname, lastInitial].filter(Boolean).join(" ")
}

/**
 * La séance nommée en entier — « Jeudi 17 septembre · 20h00 · Mathématiques avec Camille ».
 *
 * C'est ce qui titre une surface qui parle D'UNE séance (la feuille d'actions) : le parent doit
 * reconnaître laquelle sans avoir à se souvenir de la ligne qu'il vient de toucher. Toutes ses
 * matières y sont nommées. Multi-tuteur est le cas normal : le prénom se colle donc à la matière ;
 * une séance sans matière renseignée donne « … · avec Camille » plutôt qu'un séparateur orphelin.
 */
export function sessionTitle(session: Pick<UILiveSession, "date" | "subjects" | "tutor">): string {
    const subjects = session.subjects.map((s) => s.name).join(" · ")
    const withTutor = `avec ${session.tutor.firstname}`
    return `${formatDayTime(session.date)} · ${subjects !== "" ? `${subjects} ${withTutor}` : withTutor}`
}

/**
 * Une pile de matières ne montre jamais plus de trois éléments : toutes jusqu'à trois, puis deux et
 * « +N ». `<SubjectBadges max>` tient la pile ; le texte d'une séance compte pareil (`subjectSummary`).
 */
export const SUBJECT_STACK_MAX = 3

const NBSP = "\u00A0"
/** Collé derrière un trait d'union, il empêche la ligne de se couper après lui (« Physique-|Chimie »). */
const WORD_JOINER = "\u2060"
/** Le point médian reste au bout de la ligne qu'il ferme : la coupure ne tombe qu'après lui. */
const BETWEEN_INFOS = `${NBSP}· `

/** Un morceau qui ne se coupe pas : ses espaces sont insécables, ses traits d'union aussi. */
function unbroken(text: string): string {
    return text.replace(/ /g, NBSP).replace(/-/g, `-${WORD_JOINER}`)
}

/**
 * Les matières d'une séance écrites comme sa pile les montre (`<SubjectBadges>`) — le même compte
 * des deux côtés, pour que l'œil ne lise qu'un nombre :
 *  - une à trois : tous les noms (« Maths · Physique-Chimie · Français ») ;
 *  - quatre et plus : deux noms et « +N » (« Maths, Physique-Chimie +4 »).
 * Dans l'ordre où elles arrivent. Le texte ne se coupe qu'entre deux matières : un nom et son
 * « +N » tiennent ensemble (`unbroken`). La liste entière reste lisible dans le titre de la feuille
 * d'actions de la séance (`sessionTitle`).
 */
export function subjectSummary(subjects: UISubject[]): string {
    if (subjects.length <= SUBJECT_STACK_MAX) return subjects.map((s) => unbroken(s.name)).join(BETWEEN_INFOS)
    const kept = subjects.slice(0, SUBJECT_STACK_MAX - 1)
    return `${kept.map((s) => unbroken(s.name)).join(", ")}${NBSP}+${subjects.length - kept.length}`
}

/**
 * La 2ᵉ ligne d'une séance, partout où elle s'affiche — la carte de la prochaine séance, les
 * lignes de « À venir », de « Tous mes lives » et de « Quelle séance ? » : ses matières, son
 * tuteur, sa durée, dans cet ordre — « Maths · Physique-Chimie · avec Wajih · 1h ». Sans matière,
 * elle commence au tuteur.
 * Elle ne se coupe qu'entre deux informations. `tutor: false` : sans le tuteur, quand la ligne du
 * dessus le nomme déjà (l'en-tête du live). `extra` : ce que la ligne dit en plus, après les
 * matières — le masteur lit sur la sienne le type de cours et l'école (« Cours partagé · Collège
 * Jean-Moulin · 1h ») ; rien par défaut. `subjects: false` : sans les matières, quand chaque élève
 * écrit les siennes (`tutorStudentLabel`).
 */
const NO_EXTRA: string[] = []

export function sessionDetailsLabel(
    session: Pick<UILiveSession, "subjects" | "tutor" | "duration">,
    options: { tutor?: boolean; extra?: string[]; subjects?: boolean } = {}
): string {
    const { tutor = true, extra = NO_EXTRA, subjects = true } = options
    return [
        subjects ? subjectSummary(session.subjects) : "",
        ...extra.map((part) => unbroken(part)),
        tutor ? unbroken(`avec ${session.tutor.firstname}`) : "",
        unbroken(formatDuration(session.duration)),
    ]
        .filter((part) => part !== "")
        .join(BETWEEN_INFOS)
}

/**
 * Un élève d'une séance du masteur, sur sa ligne : « Jules M. · 4ᵉ · Maths ». La classe et les
 * matières sont les SIENNES (Wajih, 29/10 : dans un cours partagé, la matière n'est pas celle du
 * live) ; chacune tombe quand elle manque. Ne se coupe qu'entre deux informations, comme la ligne
 * de la séance.
 */
export function tutorStudentLabel(student: Pick<UITutorSessionStudent, "name" | "className" | "subjects">): string {
    return [unbroken(student.name), unbroken(student.className ?? ""), subjectSummary(student.subjects ?? [])]
        .filter((part) => part !== "")
        .join(BETWEEN_INFOS)
}

/**
 * Les matières d'une séance du masteur s'écrivent avec ses élèves dès qu'un seul en porte : la ligne
 * de la séance ne les redit pas. Aucun élève n'en porte (un essai sans élève encore) : la ligne de la
 * séance garde les siennes, pour que la matière ne disparaisse pas.
 */
export function subjectsByStudent(students: ReadonlyArray<Pick<UITutorSessionStudent, "subjects">>): boolean {
    return students.some((student) => (student.subjects?.length ?? 0) > 0)
}

/**
 * Les élèves d'une séance du masteur tels que sa ligne les écrit. Dès qu'un élève porte ses matières,
 * la ligne de la séance ne dit plus les siennes (`subjectsByStudent`) : un élève sans matière à côté
 * prend alors celles de la séance, pour qu'aucune matière ne disparaisse. Personne n'en porte : les
 * élèves restent tels quels, la séance garde les siennes.
 */
export function tutorRosterStudents<S extends Pick<UITutorSessionStudent, "subjects">>(session: {
    subjects: UILiveSession["subjects"]
    students: S[]
}): S[] {
    const { students, subjects } = session
    if (!subjectsByStudent(students)) return students
    return students.map((student) => ((student.subjects?.length ?? 0) > 0 ? student : { ...student, subjects }))
}

/**
 * Les AUTRES élèves connectés à un live, à partir du compte de tous, moi compris : « Un autre élève
 * connecté », « Deux autres élèves connectés » — le nombre en toutes lettres, jamais « +3 » ni « 3 élèves »
 * qui me compte (Wajih, daily du 23/10). Seul, rien (Wajih, 20/10) : je sais que je suis là.
 */
export function connectedStudentsLabel(count: number | null | undefined): string {
    const others = (count ?? 0) - 1
    if (others < 1) return ""
    return others === 1
        ? "Un autre élève connecté"
        : `${capitalizeFirst(numberInWords(others))} autres élèves connectés`
}

/**
 * La 1ʳᵉ ligne de l'en-tête d'un live au grand écran (Wajih, 20/10) : l'heure et le tuteur — « 9h20 avec
 * Gabriel ». L'heure se pose à part (en gras) : seul le reste est rendu ici, « avec Gabriel ». Les élèves
 * connectés ont leur place à eux, avec la pastille du direct (`connectedStudentsLabel`).
 */
export function liveHeadingRest(tutorFirstname: string): string {
    return tutorFirstname.trim() !== "" ? `avec ${tutorFirstname}` : ""
}
